import { FC, useLayoutEffect, useRef } from "react";
import styled from "styled-components";

const StyledContainer = styled.div`
  position: relative;
`;

const StyledPrefix = styled.span`
  position: absolute;
  pointer-events: none;
  left: 0.75rem;
  transform: translateY(-50%);
  top: 50%;
  color: var(--hero-text);
`;

const StyledInput = styled.input`
  &&& { /** required to override bootstrap's own padding */
    padding-left: calc(0.75rem + 1em);
  }
`;

const toDigits = (value: string) => value.replace(/\D/g, "");

/**
 * Whole-dollar amounts are all the bond forms ask for, so the value stays a
 * number and the grouping separators are rebuilt from it on every keystroke.
 */
export const formatCurrencyInput = (value: any) =>
  value === undefined || value === null || value === ""
    ? ""
    : Number(value).toLocaleString("en-US");

/**
 * Where the caret lands after `digitCount` digits, skipping the grouping
 * separators the reformat added or removed.
 */
const caretAfterDigits = (formatted: string, digitCount: number) => {
  let seen = 0;

  for (let i = 0; i < formatted.length; i++) {
    if (seen === digitCount) return i;
    if (/\d/.test(formatted[i])) seen++;
  }

  return formatted.length;
};

const CurrencyWidget: FC<any> = (props: any) => {
  const { id, value, required, disabled, readonly, autofocus, placeholder } =
    props;
  const inputRef = useRef<HTMLInputElement>(null);
  const digitsBeforeCaret = useRef<number | null>(null);

  // Reformatting replaces the input's value, which sends the caret to the end;
  // put it back after the same digit it followed before the edit.
  useLayoutEffect(() => {
    const input = inputRef.current;

    if (!input || digitsBeforeCaret.current === null) return;

    const caret = caretAfterDigits(input.value, digitsBeforeCaret.current);

    input.setSelectionRange(caret, caret);
    digitsBeforeCaret.current = null;
  }, [value]);

  const handleChange = ({
    target
  }: React.ChangeEvent<HTMLInputElement>) => {
    const digits = toDigits(target.value);

    digitsBeforeCaret.current = toDigits(
      target.value.slice(0, target.selectionStart ?? target.value.length)
    ).length;

    props.onChange(digits === "" ? undefined : Number(digits));
  };

  return (
    <StyledContainer>
      <StyledPrefix aria-hidden="true">$</StyledPrefix>
      <StyledInput
        ref={inputRef}
        id={id}
        className="form-control"
        type="text"
        inputMode="numeric"
        value={formatCurrencyInput(value)}
        required={required}
        disabled={disabled}
        readOnly={readonly}
        autoFocus={autofocus}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={event => props.onBlur?.(id, toDigits(event.target.value))}
        onFocus={event => props.onFocus?.(id, toDigits(event.target.value))}
      />
    </StyledContainer>
  );
};

export default CurrencyWidget;
